import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '../../context/AuthContext';
import { Layout } from '../../components/Layout';
import { BusinessHome } from './BusinessHome';
import { BusinessSubAccounts } from './BusinessSubAccounts';
import { BusinessTransactions } from './BusinessTransactions';
import { BusinessSettings } from './BusinessSettings';
import { BusinessProfile } from './BusinessProfile';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { API } from '../../utils/api';

function SwishMerchantPopup({ open, onClose, headers }) {
  const { t } = useTranslation();
  const [merchantNumber, setMerchantNumber] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [loading, setLoading] = useState(false);

  const handleAdd = async () => {
    if (!merchantNumber.trim()) return toast.error(t('auth.swishMerchantNumber') + ' is required');
    setLoading(true);
    try {
      await axios.post(`${API}/business/swish-accounts`, {
        merchant_number: merchantNumber.trim(),
        display_name: displayName.trim() || merchantNumber.trim(),
        is_primary: true,
      }, { headers });
      toast.success('Swish merchant account added!');
      onClose(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add Swish account');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(false); }}>
      <DialogContent className="max-w-md" data-testid="swish-merchant-popup">
        <DialogHeader>
          <DialogTitle>{t('auth.addSwishMerchant')}</DialogTitle>
          <DialogDescription>{t('auth.addSwishMerchantMsg')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label>{t('auth.swishMerchantNumber')} *</Label>
            <Input
              data-testid="popup-swish-merchant-input"
              value={merchantNumber}
              onChange={(e) => setMerchantNumber(e.target.value.replace(/\D/g, '').slice(0, 11))}
              placeholder={t('auth.swishMerchantPlaceholder')}
            />
            <p className="text-xs text-muted-foreground mt-1">Must start with 123 (10-11 digits)</p>
          </div>
          <div>
            <Label>Display Name</Label>
            <Input
              data-testid="popup-swish-display-name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="e.g., Main Account"
            />
          </div>
        </div>
        <DialogFooter className="flex-col gap-2 sm:flex-row">
          <Button onClick={handleAdd} disabled={loading || !merchantNumber.trim()} data-testid="popup-swish-add-btn">
            {loading ? 'Adding...' : t('common.add')}
          </Button>
          <Button variant="ghost" onClick={() => onClose(false)} data-testid="popup-swish-skip-btn">
            Skip for now
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function BusinessDashboard() {
  const { headers, user } = useAuthContext();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('home');
  const [navParams, setNavParams] = useState({});
  const [showSwishPopup, setShowSwishPopup] = useState(false);
  const [swishChecked, setSwishChecked] = useState(false);

  // Check if business has 0 Swish accounts on first load
  useEffect(() => {
    if (!headers || swishChecked) return;
    const checkSwish = async () => {
      try {
        const res = await axios.get(`${API}/business/swish-accounts`, { headers });
        if (res.data.count === 0) {
          setShowSwishPopup(true);
        }
      } catch (_) {}
      setSwishChecked(true);
    };
    checkSwish();
  }, [headers, swishChecked]);

  const handleNavigate = useCallback((tab, params = {}) => {
    setNavParams(params);
    setActiveTab(tab);
  }, []);

  // Map 'history' to 'transactions' tab
  const resolvedTab = activeTab === 'history' ? 'transactions' : activeTab;

  return (
    <Layout
      activeTab={resolvedTab}
      setActiveTab={(tab) => { setNavParams({}); setActiveTab(tab); }}
      role="business"
    >
      {/* Swish Merchant Number popup for new business accounts */}
      <SwishMerchantPopup 
        open={showSwishPopup} 
        onClose={(added) => { 
          setShowSwishPopup(false);
          if (added) setActiveTab('profile');
        }} 
        headers={headers} 
      />
      
      {resolvedTab === 'home' && <BusinessHome onNavigate={handleNavigate} />}
      {resolvedTab === 'sub-accounts' && <BusinessSubAccounts initialSubAccountId={navParams.subAccountId} />}
      {resolvedTab === 'transactions' && <BusinessTransactions initialSubAccountFilter={navParams.subAccountFilter} />}
      {resolvedTab === 'settings' && <BusinessSettings />}
      {resolvedTab === 'profile' && <BusinessProfile />}
    </Layout>
  );
}
